export type LogIsh = {
  (...args: unknown[]): void;
  log(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
  debug(...args: unknown[]): void;
  // TODO - forgot if getChild has other args...
  getChild(namespace: string, ...args: unknown[]): LogIsh;
};

function fallback(
  this: typeof globalThis.console,
  fn: (...args: unknown[]) => void,
  ...args: unknown[]
) {
  const first =
    typeof args[0] === 'string'
      ? `host-entry (global console): ${args.shift()}`
      : 'host-entry (global console)';

  fn.call(this, first, ...args);
}

export const FALLBACK_CONSOLE_LOGISH: LogIsh = Object.assign(
  fallback.bind(console, console.log),
  {
    log: fallback.bind(console, console.log),
    warn: fallback.bind(console, console.warn),
    error: fallback.bind(console, console.error),
    debug: fallback.bind(console, console.debug),
    getChild(namespace: string, ...args: unknown[]): LogIsh {
      console.error(
        'caller expected @dialpad/logger, but had to settle for global console',
        "(you should probably initialize the app's logger sooner...)",
        { namespace, args },
      );
      return FALLBACK_CONSOLE_LOGISH;
    },
  },
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isLogIsh(maybeLogger: any): maybeLogger is LogIsh {
  return (
    maybeLogger != null &&
    typeof maybeLogger === 'function' &&
    typeof maybeLogger.log === 'function' &&
    typeof maybeLogger.warn === 'function' &&
    typeof maybeLogger.error === 'function' &&
    typeof maybeLogger.debug === 'function'
  );
}

/**
 * some utilities used to grab a theoretically-provided logger
 * @internal (not exported from this package)
 * @param maybeLogger — everything that hooks have provided during host init
 * @returns — a @dialpad/logger to use during host init, or null if one has not
 *   been provided yet (indicating that context should keep using our global-
 *   console-based fallback object)
 */
export function getLogger(maybeLogger: unknown): LogIsh | null {
  try {
    if (isLogIsh(maybeLogger)) {
      const hopefullyChildlogger = maybeLogger.getChild('host-entry');
      if (isLogIsh(hopefullyChildlogger)) return hopefullyChildlogger;
      maybeLogger.warn(
        `provided logger doesn't seem like a @dialpad/logger (using it anyway)`,
      );
      return maybeLogger;
    }
  } catch (ex) {
    FALLBACK_CONSOLE_LOGISH.error(
      'exception while accessing context.logger',
      ex,
    );
  }
  return null;
}
